// src/services/api.js — Axios instance with JWT injection & error handling
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('md_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global response error handler
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('md_token');
      localStorage.removeItem('md_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:    (data)  => api.post('/auth/login', data),
  me:       ()      => api.get('/auth/me'),
  register: (data)  => api.post('/auth/register', data),
};

// ── Medicines ─────────────────────────────────────────────────────────────────
export const medicineAPI = {
  getAll:       (params) => api.get('/medicines', { params }),
  getById:      (id)     => api.get(`/medicines/${id}`),
  create:       (data)   => api.post('/medicines', data),
  update:       (id, d)  => api.put(`/medicines/${id}`, d),
  delete:       (id)     => api.delete(`/medicines/${id}`),

  getCategories:      ()       => api.get('/medicines/categories'),
  createCategory:     (data)   => api.post('/medicines/categories', data),
  updateCategory:     (id, d)  => api.put(`/medicines/categories/${id}`, d),
  deleteCategory:     (id)     => api.delete(`/medicines/categories/${id}`),

  getSubcategories:   (params) => api.get('/medicines/subcategories', { params }),
  createSubcategory:  (data)   => api.post('/medicines/subcategories', data),
  updateSubcategory:  (id, d)  => api.put(`/medicines/subcategories/${id}`, d),
  deleteSubcategory:  (id)     => api.delete(`/medicines/subcategories/${id}`),
};

// ── Dosage ────────────────────────────────────────────────────────────────────
export const dosageAPI = {
  calculate:      (data)   => api.post('/dose/calculate', data),
  getRules:       (params) => api.get('/dose/rules', { params }),
  createRule:     (data)   => api.post('/dose/rules', data),
  updateRule:     (id, d)  => api.put(`/dose/rules/${id}`, d),
  deleteRule:     (id)     => api.delete(`/dose/rules/${id}`),
  getWarnings:    (params) => api.get('/dose/warnings', { params }),
  createWarning:  (data)   => api.post('/dose/warnings', data),
  updateWarning:  (id, d)  => api.put(`/dose/warnings/${id}`, d),
  deleteWarning:  (id)     => api.delete(`/dose/warnings/${id}`),
};

// ── Tags ──────────────────────────────────────────────────────────────────────
export const tagAPI = {
  getAll:  ()      => api.get('/tags'),
  create:  (data)  => api.post('/tags', data),
  update:  (id, d) => api.put(`/tags/${id}`, d),
  delete:  (id)    => api.delete(`/tags/${id}`),
};

// ── Config ────────────────────────────────────────────────────────────────────
export const configAPI = {
  getAll:  (params) => api.get('/config', { params }),
  create:  (data)   => api.post('/config', data),
  update:  (id, d)  => api.put(`/config/${id}`, d),
  delete:  (id)     => api.delete(`/config/${id}`),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const userAPI = {
  getAll:    ()      => api.get('/users'),
  getById:   (id)    => api.get(`/users/${id}`),
  update:    (id, d) => api.put(`/users/${id}`, d),
  delete:    (id)    => api.delete(`/users/${id}`),
  getRoles:  ()      => api.get('/users/roles/all'),
  createRole:(data)  => api.post('/users/roles', data),
};

// ── Calculators ───────────────────────────────────────────────────────────────
export const calculatorAPI = {
  getAll:    ()           => api.get('/calculators'),
  calculate: (slug, data) => api.post(`/calculators/${slug}/calculate`, data),
};

// ── Logs ──────────────────────────────────────────────────────────────────────
export const logAPI = {
  getMedications: (params) => api.get('/logs/medications', { params }),
  getDashboard:   ()       => api.get('/logs/dashboard'),
};

export default api;
